import { useState, useEffect } from "react";
import { Volume2, Square, Mic2, Play, Check, ChevronDown } from "lucide-react";
import { useSpeechSynthesis } from "../hooks/useSpeech";
import { useLang, tr } from "../lib/i18n";

const SPEEDS = [0.8, 1, 1.2];
const voiceKey = (lang) => `amd-voice-${lang}`;
const readVoice = (lang) => {
  try { return localStorage.getItem(voiceKey(lang)) || ""; } catch { return ""; }
};

export const AudioPlayer = ({ text, lang: langProp }) => {
  const { lang: uiLang } = useLang();
  const lang = langProp || uiLang;
  const t = (k) => tr(lang, k);
  const { supported, speaking, speak, stop, preview, voices } = useSpeechSynthesis(lang);
  const [rate, setRate] = useState(1);
  const [voiceURI, setVoiceURI] = useState(() => readVoice(langProp || uiLang));
  const [voicesOpen, setVoicesOpen] = useState(false);

  // Restore the visitor's remembered voice for this language.
  useEffect(() => {
    setVoiceURI(readVoice(lang));
  }, [lang]);

  if (!supported) return null;

  const chooseVoice = (uri) => {
    setVoiceURI(uri);
    try { localStorage.setItem(voiceKey(lang), uri); } catch {}
    if (speaking) speak(text, rate, uri || null);
  };

  const play = () => speak(text, rate, voiceURI || null);
  const selectedVoice = voices.find((v) => v.voiceURI === voiceURI);

  return (
    <div
      className="bg-sky-50 border-2 border-sky-300 rounded-2xl p-4 space-y-3"
      data-testid="audio-player"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          {!speaking ? (
            <button
              onClick={play}
              data-testid="audio-listen-button"
              className="inline-flex items-center gap-2 bg-sky-600 hover:bg-sky-700 text-white font-bold text-lg px-5 py-3 rounded-xl transition-transform active:scale-95 min-h-[52px]"
            >
              <Volume2 className="w-5 h-5" /> {t("listen_answer")}
            </button>
          ) : (
            <button
              onClick={stop}
              data-testid="audio-stop-button"
              className="inline-flex items-center gap-2 bg-slate-700 hover:bg-slate-800 text-white font-bold text-lg px-5 py-3 rounded-xl transition-transform active:scale-95 min-h-[52px]"
            >
              <Square className="w-5 h-5" /> {t("stop")}
            </button>
          )}
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-sky-800">{t("speed")}</span>
          {SPEEDS.map((s) => (
            <button
              key={s}
              onClick={() => {
                setRate(s);
                if (speaking) speak(text, s, voiceURI || null);
              }}
              data-testid={`audio-speed-${s}`}
              className={`px-3 py-2 rounded-lg text-base font-bold transition-colors min-h-[44px] ${
                rate === s
                  ? "bg-sky-600 text-white"
                  : "bg-white text-sky-700 border border-sky-300 hover:bg-sky-100"
              }`}
            >
              {s}x
            </button>
          ))}
        </div>
      </div>

      {voices.length > 1 && (
        <div className="space-y-2" data-testid="voice-section">
          <button
            onClick={() => setVoicesOpen((o) => !o)}
            data-testid="voice-toggle"
            className="flex w-full items-center gap-2 text-left"
          >
            <Mic2 className="w-4 h-4 text-sky-700 shrink-0" />
            <span className="text-sm font-semibold text-sky-800 shrink-0">{t("voice")}</span>
            <span className="flex-1 min-w-0 truncate text-sm text-sky-900">
              {selectedVoice ? `${selectedVoice.name}` : t("default_voice")}
            </span>
            <ChevronDown
              className={`w-4 h-4 text-sky-700 shrink-0 transition-transform ${voicesOpen ? "rotate-180" : ""}`}
            />
          </button>

          {voicesOpen && (
            <div
              className="max-h-56 overflow-y-auto rounded-xl border border-sky-200 bg-white divide-y divide-sky-100"
              data-testid="voice-list"
            >
              {/* Default voice row */}
              <div className="flex items-center gap-2 px-3 py-2">
                <button
                  onClick={() => chooseVoice("")}
                  data-testid="voice-option-default"
                  className={`flex flex-1 items-center gap-2 text-left text-base min-h-[36px] ${
                    voiceURI === "" ? "font-bold text-sky-800" : "text-slate-700"
                  }`}
                >
                  {voiceURI === "" ? <Check className="w-4 h-4 text-sky-600 shrink-0" /> : <span className="w-4 shrink-0" />}
                  <span className="truncate">{t("default_voice")}</span>
                </button>
                <button
                  onClick={() => preview(t("voice_sample"), null)}
                  data-testid="voice-preview-default"
                  aria-label={t("preview_voice")}
                  className="inline-flex items-center gap-1.5 bg-sky-50 hover:bg-sky-100 text-sky-700 border border-sky-200 rounded-lg px-3 py-1.5 text-sm font-semibold shrink-0 transition-colors min-h-[36px]"
                >
                  <Play className="w-4 h-4" /> {t("preview_voice")}
                </button>
              </div>

              {voices.map((v, i) => (
                <div key={v.voiceURI} className="flex items-center gap-2 px-3 py-2">
                  <button
                    onClick={() => chooseVoice(v.voiceURI)}
                    data-testid={`voice-option-${i}`}
                    className={`flex flex-1 items-center gap-2 text-left text-base min-h-[36px] ${
                      voiceURI === v.voiceURI ? "font-bold text-sky-800" : "text-slate-700"
                    }`}
                  >
                    {voiceURI === v.voiceURI ? <Check className="w-4 h-4 text-sky-600 shrink-0" /> : <span className="w-4 shrink-0" />}
                    <span className="truncate">
                      {v.name} <span className="text-slate-400">({v.lang})</span>
                    </span>
                  </button>
                  <button
                    onClick={() => preview(t("voice_sample"), v.voiceURI)}
                    data-testid={`voice-preview-${i}`}
                    aria-label={t("preview_voice")}
                    className="inline-flex items-center gap-1.5 bg-sky-50 hover:bg-sky-100 text-sky-700 border border-sky-200 rounded-lg px-3 py-1.5 text-sm font-semibold shrink-0 transition-colors min-h-[36px]"
                  >
                    <Play className="w-4 h-4" /> {t("preview_voice")}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
